import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { OperatorsService } from '../../../shared/services/operators.service';
import {
  AdminUpdateOperatorRequest,
  Operator,
} from '../../../shared/types/operator.types';
import { OperatorFormComponent } from '../operator-form/operator-form.component';

@Component({
  selector: 'app-operator-edit',
  imports: [PageHeaderComponent, DataStateComponent, OperatorFormComponent],
  templateUrl: './operator-edit.component.html',
})
export class OperatorEditComponent implements OnInit {
  private readonly service = inject(OperatorsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly op = signal<Operator | null>(null);

  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.service
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (o) => { this.op.set(o); this.loading.set(false); },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(toErrorMessage(err, 'Impossible de charger cet opérateur.'));
        },
      });
  }

  onSave(payload: AdminUpdateOperatorRequest): void {
    this.submitting.set(true);
    this.serverError.set(null);
    this.service
      .update(this.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          void this.router.navigate(['/operators', this.id]);
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.serverError.set(
            toErrorMessage(err, 'errors.updateFailed', {
              403: "Cet opérateur est géré par le système et n'est pas éditable.",
            }),
          );
        },
      });
  }

  onCancel(): void {
    void this.router.navigate(['/operators', this.id]);
  }
}
